/// <reference types="vite/client" />

import { useObserve, useValue } from "@legendapp/state/react";
import { PostHogProvider } from "@posthog/react";
import appCss from "@repo/ui/globals.css?url";
import { ActionProvider } from "@repo/ui/lib/actions/action-registry";
import { SidebarProvider } from "@repo/ui/ui/sidebar";
import { Toaster } from "@repo/ui/ui/sonner";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  Scripts,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import { GTProvider } from "gt-react";
import { useEffect, useRef } from "react";

import { isChatPaneRoute } from "~/components/app/chat/chat-route";
import { CommandPalette } from "~/components/command-palette";
import { DocumentTitle } from "~/components/document-title";
import { GlobalActions } from "~/components/global-actions";
import { TopBar } from "~/components/header";
import { PendingComponent } from "~/components/pending";
import { isPostHogEnabled, posthog } from "~/libs/posthog.lib";
import { queryClient } from "~/libs/query-client";
import loadTranslations from "~/loadTranslations";
import { AppShell } from "~/pages/app-shell";
import type { RouterContext } from "~/router";
import { auth$, type Session, type User, user$ } from "~/stores/auth";
import { chat$ } from "~/stores/chat";
import { theme$ } from "~/stores/theme.store";
import gtConfig from "../../gt.config.json";

/**
 * Themed Sonner host. Shifts toasts left of the AI chat sidepane when it's
 * effectively open (chat-eligible route + `chat$.isOpen`) so toasts don't
 * cover the panel. Mobile uses the chat's overlay Sheet, so `mobileOffset`
 * is left at the Sonner default.
 */
const ThemedToaster = () => {
  const theme = useValue(theme$.theme);
  const isChatRoute = useRouterState({
    select: (s) => isChatPaneRoute(s.location.pathname),
  });
  const userWantsOpen = useValue(chat$.isOpen);
  const chatOpen = isChatRoute && userWantsOpen;
  const chatWidth = useValue(chat$.width);
  // Chat panel sits at right-2 (8px) and is `chatWidth - 8px` wide, so its
  // left edge sits at `chatWidth` from the right viewport edge. Add the same
  // 16px gap used when the panel is closed.
  return (
    <Toaster
      theme={theme}
      offset={{
        right: chatOpen ? `${chatWidth + 16}px` : "16px",
        bottom: "16px",
      }}
    />
  );
};

/**
 * Root route error fallback. TanStack Router catches route/render errors in its
 * internal `CatchBoundary`, so they never reach the `Sentry.reactErrorHandler`
 * handlers in `client.tsx`. Logging via `console.error` makes the error visible
 * in the prod browser console and reaches Sentry through the
 * `consoleLoggingIntegration` in `sentry.lib.ts`.
 */
const RootErrorComponent = ({ error }: { error: Error }) => {
  useEffect(() => {
    console.error("Root route error", error);
  }, [error]);
  return <div>Error</div>;
};

export const Route = createRootRouteWithContext<RouterContext>()({
  ssr: false,
  head: () => {
    return {
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1.0" },
        { title: "Ask questions about your data | Mora" },
        {
          name: "description",
          content:
            "Ask questions about your data and get instant AI-powered insights. Mora helps you explore, analyze, and understand your data through natural language queries.",
        },
        { name: "apple-mobile-web-app-title", content: "Mora" },
        {
          property: "og:title",
          content: "Ask questions about your data | Mora",
        },
        {
          property: "og:description",
          content:
            "Ask questions about your data and get instant AI-powered insights. Mora helps you explore, analyze, and understand your data through natural language queries.",
        },
        { property: "og:type", content: "website" },
        { property: "og:image", content: "/image/cover.png" },
        {
          property: "og:image:alt",
          content: "Mora product interface showcasing instant insights",
        },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:site", content: "@IndexBI" },
        {
          name: "twitter:title",
          content: "Ask AI Questions About Your Data | Mora",
        },
        {
          name: "twitter:description",
          content:
            "Ask questions about your data and get instant AI-powered insights. Mora helps you explore, analyze, and understand your data through natural language queries.",
        },
        { name: "twitter:image", content: "/image/cover.png" },
      ],
      links: [
        {
          rel: "apple-touch-icon",
          sizes: "180x180",
          href: "/apple-touch-icon.png",
        },
        {
          rel: "icon",
          type: "image/png",
          sizes: "32x32",
          href: "/favicon-32x32.png",
        },
        {
          rel: "icon",
          type: "image/png",
          sizes: "16x16",
          href: "/favicon-16x16.png",
        },
        { rel: "manifest", href: "/site.webmanifest" },
        { rel: "preload", as: "style", href: appCss },
        { rel: "stylesheet", href: appCss },
        {
          rel: "preconnect",
          href: "https://fonts.googleapis.com",
        },
        {
          rel: "preconnect",
          href: "https://fonts.gstatic.com",
          crossOrigin: "anonymous",
        },
        {
          rel: "stylesheet",
          href: "https://fonts.googleapis.com/css2?family=Geist+Mono:wght@100..900&display=swap",
        },
      ],
      scripts: [
        {
          children: `(function(d, script) {
  script = d.createElement('script');
  script.async = false;
  script.onload = function(){
    Plain.init({
      appId: 'liveChatApp_01KR4FD5GS5YD30PESVWP63XTQ',
      hideLauncher: true,
    });
  };
  script.src = 'https://chat.cdn-plain.com/index.js';
  d.getElementsByTagName('head')[0].appendChild(script);
}(document));`,
        },
      ],
    };
  },
  pendingComponent: PendingComponent,
  notFoundComponent: () => <div>Not Found</div>,
  errorComponent: RootErrorComponent,
  beforeLoad: () => {
    // Synchronous on purpose. An async `beforeLoad` on the root match
    // puts `__root__` into `status: "pending"`, and TSR has a race
    // window between that status flip and `_nonReactive.loadPromise`
    // being assigned. `MatchInner` executes
    // `throw getMatchPromise(match, "loadPromise")` for any pending
    // match, which evaluates to a literal `throw undefined` when the
    // slot is empty -- TSR's `CatchBoundary` does `if (error)` so
    // `undefined` falls through, and React 19 surfaces it as a blank
    // document. Keeping every `beforeLoad` in the tree synchronous
    // closes the race entirely.
    //
    // The async work that used to live here is initialized elsewhere:
    //  - `duckdb.init()` self-initiates at module load (see
    //    `apps/web/src/workers/duckdb.ts`); every public duckdb method
    //    awaits the same dedup'd init promise internally.
    //  - `authClient.getSession()` fires at module load from
    //    `apps/web/src/stores/auth/auth.store.ts`; better-auth's
    //    `session` atom subscription feeds the result into `auth$`,
    //    which is persisted via `ObservablePersistLocalStorage` and
    //    hydrates synchronously for returning users. Cold tabs with no
    //    cache read `null` here, the `_app` gate bounces to `/login`,
    //    and `app-shell.tsx` invalidates the router when `auth$`
    //    resolves so the user is routed back.
    const authValue = auth$.get() as Session | null;
    return {
      session: authValue?.session ?? null,
      user: (authValue?.user as User | undefined) ?? null,
    };
  },
  shellComponent: ({ children }) => {
    const theme = useValue(theme$.theme);
    return (
      <html lang="en" className={theme}>
        <head>
          <HeadContent />
        </head>
        <body
          className="flex h-svh flex-col font-sans antialiased"
          style={
            {
              "--font-mono": "'Geist Mono', monospace",
            } as React.CSSProperties
          }
        >
          {children}
          <Scripts />
        </body>
      </html>
    );
  },
  component: () => {
    const userId = useValue(user$.id);
    // Session lifecycle safety net. Mounted at the root so it runs on every
    // route -- crucially `/login` and any other non-`_app`
    // surface where auth might flip. Two cases:
    //
    // - **Login on `/login`**: `signInWithEmail` resolves, better-auth's
    //   session atom updates, our subscription writes `auth$.set(...)`, and
    //   `user$.id` flips from "" to the new id. The `!prev` branch below
    //   invalidates the router so `__root.beforeLoad` re-runs synchronously
    //   with the populated `auth$`, and `/login.beforeLoad` then emits the
    //   `search.redirect ?? "/"` redirect. Without this hook the
    //   `navigate({ to: "/" })` in `login.page.tsx` races a stale `__root`
    //   context (cached `user: null`) and `_app/route.beforeLoad` bounces
    //   straight back to `/login` -- only a manual refresh recovers.
    // - **Logout / 401 mid-tab**: `user$.id` flips to "". Invalidate so
    //   `_app/route.beforeLoad` re-runs and emits its own `/login` redirect
    //   (firing a concurrent `navigate()` here would race the redirect
    //   commit and could surface as `throw undefined` in `MatchInner`).
    const router = useRouter();
    const previousIdRef = useRef<string | undefined>(undefined);
    useObserve(() => {
      const id = user$.id.get();
      const prev = previousIdRef.current;
      previousIdRef.current = id;
      if (!id) {
        queueMicrotask(() => router.invalidate());
        return;
      }
      if (!prev) {
        queueMicrotask(() => router.invalidate());
      }
    });

    const tree = (
      <GTProvider config={gtConfig} loadTranslations={loadTranslations}>
        <ActionProvider>
          <SidebarProvider
            sidebarNames={["left", "right"]}
            defaultOpen={!!userId}
            open={userId ? undefined : false}
            className="flex flex-col"
            style={
              {
                "--sidebar-width": "calc(var(--spacing) * 72)",
                "--header-height": "calc(var(--spacing) * 12)",
              } as React.CSSProperties
            }
          >
            <DocumentTitle />
            <GlobalActions />
            <CommandPalette />
            <TopBar />
            <div className="flex min-h-0 flex-1">
              <AppShell />
            </div>
            <ThemedToaster />
          </SidebarProvider>
        </ActionProvider>
      </GTProvider>
    );

    const wrappedTree = isPostHogEnabled ? (
      <PostHogProvider client={posthog}>{tree}</PostHogProvider>
    ) : (
      tree
    );

    return (
      <QueryClientProvider client={queryClient}>
        {wrappedTree}
      </QueryClientProvider>
    );
  },
});
